import React, { useState, useEffect, useRef } from 'react';
import { Box, Spinner, Text, VStack, HStack, useColorMode, useColorModeValue, IconButton } from '@chakra-ui/react';
import { SunIcon, MoonIcon } from '@chakra-ui/icons';
import './Signage.css';

const ActiveImageWithThrottle = ({ imageUrl, index, currentIndex, lastImageLoadTime, setLastImageLoadTime, onLoad, onError }) => {
  const [shouldLoad, setShouldLoad] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [imageError, setImageError] = useState(false);
  const retryTimeoutRef = useRef(null);
  const loadTimeoutRef = useRef(null);
  const prevImageUrlRef = useRef(null);
  const prevCurrentIndexRef = useRef(null);
  
  // Detect mobile device for optimized loading
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  useEffect(() => {
    const isSameImage = imageUrl === prevImageUrlRef.current && currentIndex === prevCurrentIndexRef.current;
    prevImageUrlRef.current = imageUrl;
    prevCurrentIndexRef.current = currentIndex;
    
    if (isSameImage && shouldLoad) {
      return;
    }
    
    if (!isSameImage) {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
        loadTimeoutRef.current = null;
      }
      setRetryCount(0);
      setShouldLoad(false);
      setImageError(false);
    }

    // Use shorter delay on mobile (1 second) vs desktop (2 seconds)
    // This helps prevent timeouts on slower mobile networks
    const timeSinceLastLoad = lastImageLoadTime > 0 ? Date.now() - lastImageLoadTime : 999999;
    const minDelay = isMobile ? 1000 : 2000;
    const delay = lastImageLoadTime > 0 ? Math.max(0, minDelay - timeSinceLastLoad) : 0;

    const loadTimer = setTimeout(() => {
      setShouldLoad(true);
      setLastImageLoadTime(Date.now());
    }, delay);

    return () => clearTimeout(loadTimer);
  }, [currentIndex, imageUrl, lastImageLoadTime, setLastImageLoadTime, shouldLoad, index, isMobile]);

  const handleError = (e) => {
    setImageError(true);
    
    // Notify parent component about error (for tracking expired URLs)
    if (onError) {
      onError(e);
    }
    
    if (retryCount < 3) {
      // Faster retries on mobile
      const retryDelay = isMobile 
        ? Math.pow(2, retryCount) * 2000 
        : Math.pow(2, retryCount + 1) * 5000;
      retryTimeoutRef.current = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        setImageError(false);
        setShouldLoad(true);
        setLastImageLoadTime(Date.now());
      }, retryDelay);
    }
  };

  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
    };
  }, []);


  if (!shouldLoad) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minH="100vh"
        bg="transparent"
      />
    );
  }

  if (imageError && retryCount >= 1) {
    const isGoogleDrive = imageUrl && imageUrl.includes('drive.google.com');
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minH="100vh"
        bg="transparent"
        color="white"
        p={8}
      >
        <Box textAlign="center" maxW="600px">
          <Text fontSize="lg" color="red.400" mb={2} fontWeight="bold">
            Unable to load image {index + 1}
          </Text>
          {isGoogleDrive ? (
            <>
              <Text fontSize="md" color="gray.300" mb={3}>
                Error 403: Access Denied
              </Text>
              <Text fontSize="sm" color="gray.400" mb={2}>
                Google Drive images must be publicly shared. See docs/FIX_GOOGLE_DRIVE_403.md for complete instructions.
              </Text>
              <VStack spacing={1} align="flex-start" fontSize="xs" color="gray.500" textAlign="left" mt={2}>
                <Text fontWeight="bold" color="yellow.400">Quick Fix:</Text>
                <Text>1. Open Google Drive folder</Text>
                <Text>2. Select ALL image files (Ctrl+A / Cmd+A)</Text>
                <Text>3. Right-click → Share → "Anyone with the link" → Viewer</Text>
                <Text>4. Also share the folder itself</Text>
                <Text>5. Verify: npm run verify-gdrive-sharing</Text>
                <Text>6. Regenerate: npm run generate-signage-images-gdrive</Text>
                <Text>7. Deploy: Fresh URLs will be fetched automatically</Text>
              </VStack>
            </>
          ) : (
            <Text fontSize="sm" color="gray.500">
              Check your connection or image URL
            </Text>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      width="100%"
      height="100%"
      overflow="hidden"
    >
      <img
        key={`image-${index}-${retryCount}`}
        src={imageUrl}
        alt={`Slide ${index + 1}`}
      className="signage-image"
      loading="eager"
      referrerPolicy="no-referrer"
      style={{
        maxWidth: '100%',
        maxHeight: '100%',
        width: 'auto',
        height: 'auto',
        objectFit: 'contain',
        display: 'block',
        margin: '0 auto',
        opacity: imageError ? 0.5 : 1,
        transition: 'opacity 0.3s ease',
        boxSizing: 'border-box',
        objectPosition: 'center center'
      }}
        onError={(e) => {
          // Try multiple URL format fallbacks for Google Drive
          if (imageUrl && (imageUrl.includes('drive.google.com') || imageUrl.includes('googleusercontent.com'))) {
            // Extract file ID from different URL formats
            let fileId = null;
            
            // Try to extract from thumbnail URL: lh3.googleusercontent.com/d/FILE_ID
            const thumbnailMatch = imageUrl.match(/googleusercontent\.com\/d\/([a-zA-Z0-9_-]+)/);
            if (thumbnailMatch) {
              fileId = thumbnailMatch[1];
            } else {
              // Try to extract from drive.google.com URL
              const driveMatch = imageUrl.match(/[?&]id=([a-zA-Z0-9_-]+)/);
              if (driveMatch) {
                fileId = driveMatch[1];
              }
            }
            
            if (!fileId) {
              handleError(e);
              return;
            }
            
            // Try different URL formats based on retry count
            if (retryCount === 0) {
              // First fallback: Try Google Drive viewer/thumbnail format
              const altUrl = `https://drive.google.com/thumbnail?id=${fileId}&sz=w1920`;
              e.target.src = altUrl;
              setRetryCount(1);
              setImageError(false);
              return;
            } else if (retryCount === 1) {
              // Second fallback: Try export=view format
              const altUrl = `https://drive.google.com/uc?export=view&id=${fileId}`;
              e.target.src = altUrl;
              setRetryCount(2);
              setImageError(false);
              return;
            } else if (retryCount === 2) {
              // Third fallback: Try export=download format
              const altUrl = `https://drive.google.com/uc?export=download&id=${fileId}`;
              e.target.src = altUrl;
              setRetryCount(3);
              setImageError(false);
              return;
            }
          }
          
          handleError(e);
        }}
        onLoad={(e) => {
          setRetryCount(0);
          setImageError(false);
          onLoad();
        }}
        ref={(imgElement) => {
          if (!imgElement) return;
          
          setTimeout(() => {
            if (imgElement.complete && imgElement.naturalHeight > 0) {
              setImageError(false);
              onLoad();
            } else {
              const loadHandler = () => {
                if (loadTimeoutRef.current) {
                  clearTimeout(loadTimeoutRef.current);
                  loadTimeoutRef.current = null;
                }
                setImageError(false);
                onLoad();
              };
              imgElement.addEventListener('load', loadHandler, { once: true });
              
              // Timeout for mobile - if image doesn't load within 10 seconds, show error
              loadTimeoutRef.current = setTimeout(() => {
                if (!imgElement.complete || imgElement.naturalHeight === 0) {
                  handleError({ target: imgElement });
                }
              }, isMobile ? 10000 : 15000);
              
              if (imgElement.src) {
                setTimeout(() => {
                  if (imgElement.complete && imgElement.naturalHeight > 0) {
                    if (loadTimeoutRef.current) {
                      clearTimeout(loadTimeoutRef.current);
                      loadTimeoutRef.current = null;
                    }
                    loadHandler();
                  }
                }, 100);
              }
            }
          }, 0);
        }}
      />
    </Box>
  );
};

const Signage = () => {
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [previousIndex, setPreviousIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastImageLoadTime, setLastImageLoadTime] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const errorCountRef = useRef(0);
  const transitionTimeoutRef = useRef(null);
  const { colorMode, toggleColorMode } = useColorMode();
  const bgColor = useColorModeValue('linear-gradient(135deg, #f5f5dc 0%, #e8e8d5 30%, #d4d4c4 60%, #c4c4b0 100%)', 'linear-gradient(135deg, #1a202c 0%, #2d3748 50%, #1a202c 100%)');
  const headerBg = useColorModeValue('rgba(245, 245, 220, 0.92)', 'rgba(26, 32, 44, 0.95)');
  const footerBg = useColorModeValue('rgba(245, 245, 220, 0.92)', 'rgba(26, 32, 44, 0.95)');
  const textColor = useColorModeValue('#2d3748', '#e2e8f0');
  const hoverBg = useColorModeValue('rgba(0, 0, 0, 0.1)', 'rgba(255, 255, 255, 0.1)');
  const footerTextColor = useColorModeValue('#4a5568', '#cbd5e0');
  const footerLinkColor = useColorModeValue('#718096', '#a0aec0');

  // Use Google Drive master folder approach
  const MASTER_FOLDER_ID = process.env.REACT_APP_GOOGLE_DRIVE_MASTER_FOLDER_ID;
  const SIGNAGE_FOLDER_ID = process.env.REACT_APP_SIGNAGE_FOLDER_ID;
  const SIGNAGE_SUBFOLDER_NAME = 'Signage';
  const slideDuration = parseInt(process.env.REACT_APP_SIGNAGE_SLIDE_DURATION_MS || '10000', 10);
  // Convert days to milliseconds: 1 day = 24 * 60 * 60 * 1000 = 86400000 ms
  const refreshIntervalDays = parseInt(process.env.REACT_APP_GOOGLE_DRIVE_FETCH_INTERVAL_DAYS || '1', 10);
  const refreshInterval = refreshIntervalDays * 24 * 60 * 60 * 1000;

  useEffect(() => {
    let refreshTimer = null;
    let isMounted = true;

    const loadSignageImages = async () => {
      // Try Google Drive master folder approach first (preferred)
      if (MASTER_FOLDER_ID || SIGNAGE_FOLDER_ID) {
        try {
          const { fetchImagesFromGoogleDriveFolder, findSubfolderByName } = await import('../utils/googleDriveImages');
          const hasApiKey = process.env.REACT_APP_GOOGLE_DRIVE_API_KEY && process.env.REACT_APP_GOOGLE_DRIVE_API_KEY.trim() !== '';
          
          if (!hasApiKey) {
            if (isMounted) {
              setError('Google Drive API key is missing. Please set REACT_APP_GOOGLE_DRIVE_API_KEY in your .env file and restart the development server.');
              setLoading(false);
            }
            return;
          }

          let folderId = SIGNAGE_FOLDER_ID;
          
          // If no direct folder ID, try to find subfolder in master folder
          if (!folderId && MASTER_FOLDER_ID) {
            try {
              folderId = await findSubfolderByName(MASTER_FOLDER_ID, SIGNAGE_SUBFOLDER_NAME);
            } catch (err) {
              console.error('Failed to find signage subfolder:', err);
              if (isMounted) {
                setError(err.message);
                setLoading(false);
              }
              return;
            }
          }

          if (folderId) {
            try {
              const driveImages = await fetchImagesFromGoogleDriveFolder(folderId);
              const imageUrls = driveImages.map(img => img.url);
              if (isMounted) {
                if (imageUrls.length > 0) {
                  setImages(imageUrls);
                  setCurrentIndex(0);
                  setLoading(false);
                  
                  // Set up refresh interval for Google Drive
                  refreshTimer = setInterval(async () => {
                    try {
                      const refreshedImages = await fetchImagesFromGoogleDriveFolder(folderId);
                      const refreshedUrls = refreshedImages.map(img => img.url);
                      if (refreshedUrls.length > 0 && isMounted) {
                        setImages(refreshedUrls);
                      }
                    } catch (err) {
                      console.error('Error refreshing signage images:', err);
                    }
                  }, refreshInterval);
                } else {
                  setError('No images found in Signage folder.');
                  setLoading(false);
                }
              }
              return;
            } catch (err) {
              console.error('Failed to fetch signage images from Google Drive:', err);
              if (isMounted) {
                setError(err.message);
                setLoading(false);
              }
              return;
            }
          }
        } catch (err) {
          console.error('Error loading Google Drive utilities:', err);
          // Fall through to try JSON file approach
        }
      }

      // No fallback - require Google Drive configuration
      if (isMounted) {
        setError('Google Drive configuration not found. Please set REACT_APP_GOOGLE_DRIVE_MASTER_FOLDER_ID (recommended) or REACT_APP_SIGNAGE_FOLDER_ID environment variable.');
        setLoading(false);
      }
    };

    loadSignageImages();

    return () => {
      isMounted = false;
      if (refreshTimer) {
        clearInterval(refreshTimer);
      }
    };
  }, [MASTER_FOLDER_ID, SIGNAGE_FOLDER_ID, refreshInterval]);

  useEffect(() => {
    // Disable scrolling on body when signage is active
    document.body.classList.add('signage-active');
    
    return () => {
      // Re-enable scrolling when component unmounts
      document.body.classList.remove('signage-active');
    };
  }, []);

  useEffect(() => {
    if (images.length === 0) return;

    const interval = setInterval(() => {
      // Start transition animation
      setIsTransitioning(true);
      
      // Update indices - track previous for transition
      setCurrentIndex((prev) => {
        setPreviousIndex(prev);
        return (prev + 1) % images.length;
      });
      
      // Clear transitioning state after animation completes
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
      transitionTimeoutRef.current = setTimeout(() => {
        setIsTransitioning(false);
      }, 1500); // Match CSS transition duration (1.5s)
    }, slideDuration);

    return () => {
      clearInterval(interval);
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, [images.length, slideDuration]);

  if (loading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minH="100vh"
        bg="transparent"
        color="white"
      >
        <Box textAlign="center">
          <Spinner size="xl" color="white" thickness="4px" mb={4} />
          <Text fontSize="xl">Loading images...</Text>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minH="100vh"
        bg="transparent"
        color="white"
        p={8}
      >
        <Box textAlign="center" maxW="800px">
          <Text fontSize="2xl" mb={4} color="red.400">Error</Text>
          <Text fontSize="lg">{error}</Text>
          <Text fontSize="sm" mt={4} color="gray.400">
            Please configure REACT_APP_GOOGLE_DRIVE_MASTER_FOLDER_ID (recommended) or REACT_APP_SIGNAGE_FOLDER_ID environment variable.
          </Text>
        </Box>
      </Box>
    );
  }

  if (images.length === 0) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minH="100vh"
        bg="transparent"
        color="white"
      >
        <Text fontSize="xl">No images available</Text>
      </Box>
    );
  }

  return (
    <Box className="signage-container" style={{ background: bgColor }}>
      {/* Header with Logo - Fixed height */}
      <Box
        position="absolute"
        top="0"
        left="0"
        width="100%"
        height="120px"
        zIndex="100"
        bg={headerBg}
        display="flex"
        alignItems="center"
        justifyContent="center"
        px={8}
        boxSizing="border-box"
      >
        <HStack spacing={6} align="center" justify="space-between" width="100%" maxW="1400px" px={4}>
          <HStack spacing={6} align="center" justify="center">
            <Box flexShrink={0}>
              <img
                src="/images/misc/holly_valley_logo.png"
                alt="Holly Valley Logo"
                style={{
                  height: '65px',
                  width: 'auto',
                  objectFit: 'contain',
                  maxHeight: '65px',
                  display: 'block'
                }}
              />
            </Box>
            <Text
              fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
              fontWeight="900"
              color={textColor}
              letterSpacing="0.1em"
              lineHeight="1.1"
              whiteSpace="nowrap"
            >
              HOLLY VALLEY
            </Text>
          </HStack>
          <IconButton
            aria-label="Toggle dark mode"
            icon={colorMode === 'light' ? <MoonIcon /> : <SunIcon />}
            onClick={toggleColorMode}
            variant="ghost"
            size="lg"
            color={textColor}
            _hover={{ bg: hoverBg }}
          />
        </HStack>
      </Box>

      {/* Slideshow Images - Positioned below header */}
      <Box 
        className="signage-slideshow"
        position="absolute"
        top="120px"
        left="0"
        width="100%" 
        height="calc(100vh - 15vh - 120px)"
        overflow="hidden"
        boxSizing="border-box"
      >
        {images.map((imageUrl, index) => {
          const isActive = index === currentIndex;
          const isPrevious = index === previousIndex;
          const isNext = index === (currentIndex + 1) % images.length;
          // Always render current and next slide, and previous during transition
          const shouldRender = isActive || isNext || (isPrevious && isTransitioning);
          
          // Determine transition classes during transition
          let transitionClass = '';
          if (isTransitioning) {
            if (isPrevious && previousIndex !== currentIndex) {
              // Previous slide is leaving - slide left and fade out
              transitionClass = 'leaving';
            } else if (isActive && previousIndex !== currentIndex) {
              // New slide is entering - slide in from right and fade in
              transitionClass = 'entering';
            }
          }
          
          // Build class string
          let className = 'signage-slide';
          if (isActive && !isTransitioning) className += ' active';
          if (isNext && !isActive) className += ' next';
          if (transitionClass) className += ` ${transitionClass}`;
          
          return (
            <Box
              key={index}
              className={className}
              style={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                boxSizing: 'border-box',
                overflow: 'hidden'
              }}
            >
              {shouldRender && (
                <ActiveImageWithThrottle
                  imageUrl={imageUrl}
                  index={index}
                  currentIndex={currentIndex}
                  lastImageLoadTime={lastImageLoadTime}
                  setLastImageLoadTime={setLastImageLoadTime}
                  onLoad={() => {}}
                  onError={(e) => {
                    // Track 403 errors from Google Drive URLs (especially expired CDN URLs)
                    const img = e.target;
                    if (img && img.src && (img.src.includes('googleusercontent.com') || img.src.includes('drive.google.com'))) {
                      errorCountRef.current += 1;
                      
                      // If multiple errors detected (likely expired URLs), trigger refresh from Google Drive
                      if (errorCountRef.current >= 3) {
                        setTimeout(() => {
                          // Reload images from Google Drive to get fresh URLs
                          const reloadImages = async () => {
                            try {
                              const { fetchImagesFromGoogleDriveFolder, findSubfolderByName } = await import('../utils/googleDriveImages');
                              const hasApiKey = process.env.REACT_APP_GOOGLE_DRIVE_API_KEY && process.env.REACT_APP_GOOGLE_DRIVE_API_KEY.trim() !== '';
                              
                              if (hasApiKey) {
                                let folderId = SIGNAGE_FOLDER_ID;
                                if (!folderId && MASTER_FOLDER_ID) {
                                  folderId = await findSubfolderByName(MASTER_FOLDER_ID, SIGNAGE_SUBFOLDER_NAME);
                                }
                                
                                if (folderId) {
                                  const driveImages = await fetchImagesFromGoogleDriveFolder(folderId);
                                  const imageUrls = driveImages.map(img => img.url);
                                  if (imageUrls.length > 0) {
                                    setImages(imageUrls);
                                    errorCountRef.current = 0; // Reset error count
                                  }
                                }
                              }
                            } catch (err) {
                              console.error('Error reloading images:', err);
                            }
                          };
                          reloadImages();
                        }, 10000); // Wait 10 seconds before refreshing
                      }
                    }
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      {/* Footer with Legal Disclaimers - Reduced height */}
      <Box
        position="absolute"
        bottom="0"
        left="0"
        width="100%"
        height="15vh"
        maxHeight="180px"
        zIndex="100"
        bg={footerBg}
        py={1.5}
        px={8}
        overflowY="auto"
      >
        <VStack spacing={1} align="center" h="100%" justify="center">
          {/* Contact Info */}
          <HStack spacing={4} align="center">
            <Text fontSize="xs" color={textColor}>
              Contact: <a href="tel:13363040094" style={{ color: '#41A699' }}>+1(336)304-0094</a>
            </Text>
            <Text fontSize="xs" color={footerLinkColor}>|</Text>
            <Text fontSize="xs" color={textColor}>DBA Holly Valley</Text>
          </HStack>

          {/* Legal Disclaimers - Compact */}
          <VStack spacing={0.5} align="center" maxW="1200px">
            {/* Age Restrictions */}
            <Text fontSize="xs" color="#d69e2e" fontWeight="600" textAlign="center" lineHeight="1.2">
              Age Restricted Products: Tobacco/Alcohol 21+ | Lottery 18+ | Valid ID Required
            </Text>

            {/* NC Regulations & ABC Laws Compliance - Compact */}
            <Text fontSize="9px" color={footerTextColor} textAlign="center" lineHeight="1.3">
              <strong>LEGAL NOTICE - NC REGULATIONS & ABC LAWS COMPLIANCE:</strong> This establishment complies with all NC state regulations and ABC laws regarding tobacco products and alcoholic beverages (including cold beer). All advertisements are for informational purposes only. <strong>Tobacco:</strong> 21+ only, valid ID required. <strong>Alcoholic Beverages (Cold Beer):</strong> Regulated by NC ABC Commission, 21+ only, valid ID required. <strong>NC Lottery:</strong> 18+ only, valid ID required. Play responsibly. Gambling problem? Call 1-800-522-4700.
            </Text>
            <Text fontSize="10px" color={footerLinkColor} textAlign="center" mt={1}>
              Holly Valley maintains strict compliance with all applicable regulations. 
              For more information, visit <a href="https://www.abc.nc.gov" target="_blank" rel="noopener noreferrer" style={{ color: '#41A699' }}>abc.nc.gov</a> and <a href="https://www.nclottery.com" target="_blank" rel="noopener noreferrer" style={{ color: '#41A699' }}>nclottery.com</a>
            </Text>
          </VStack>
        </VStack>
      </Box>
    </Box>
  );
};

export default Signage;
